import React, { useEffect, useState } from "react";
import { HeartHandshake, HandCoins, Users, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { timeAgo } from "@/components/PostCard";

const PRESETS = [5, 10, 25, 50];

export default function Donations() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/donations"); setData(r.data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, []);

  const pledge = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Please enter an amount");
    setBusy(true);
    try {
      await api.post("/donations", { amount: amt, message });
      toast.success("Thank you for your generosity! 🎗");
      setAmount(""); setMessage("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-terracotta flex items-center justify-center">
          <HeartHandshake size={26} strokeWidth={2.5} className="text-white" />
        </div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900">Community Fund</h1>
      </div>
      <p className="text-lg text-forest-700 mb-6 max-w-2xl">Sponsor dinners and moments of companionship for our older neighbours. Every pledge to the shared fund helps someone share a warm meal and good company.</p>

      {/* Fund summary */}
      <div className="bg-forest-900 rounded-2xl p-6 sm:p-8 mb-6 text-white" data-testid="fund-summary">
        <p className="text-cream/70 font-semibold text-sm uppercase tracking-widest">Total pledged to the fund</p>
        <p className="font-head text-5xl font-extrabold mt-1">{data ? fmt(data.total) : "—"}</p>
        <div className="flex flex-wrap gap-6 mt-5">
          <div className="flex items-center gap-2"><Users size={22} strokeWidth={2.5} className="text-gold" /><span className="font-semibold">{data ? data.supporters : "—"} supporters</span></div>
          <div className="flex items-center gap-2"><HandCoins size={22} strokeWidth={2.5} className="text-gold" /><span className="font-semibold">{data ? data.count : "—"} pledges</span></div>
          {data?.mine > 0 && <div className="flex items-center gap-2"><Sparkles size={22} strokeWidth={2.5} className="text-gold" /><span className="font-semibold">You've given {fmt(data.mine)}</span></div>}
        </div>
      </div>

      {/* Pledge form */}
      <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8 mb-6">
        <h2 className="font-head text-2xl font-bold text-forest-900 mb-4">Make a pledge</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((v) => (
            <button key={v} onClick={() => setAmount(String(v))} data-testid={`preset-${v}`}
              className={`min-h-[52px] px-6 rounded-full font-bold border-2 transition-colors ${String(v) === amount ? "bg-terracotta text-white border-terracotta" : "bg-white text-forest-700 border-border hover:border-forest-500"}`}>
              {v}
            </button>
          ))}
        </div>
        <label className="font-bold text-forest-900 mb-1.5 block">Amount</label>
        <input data-testid="donation-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="Enter an amount" className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream mb-4" />
        <label className="font-bold text-forest-900 mb-1.5 block">Message (optional)</label>
        <textarea data-testid="donation-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
          placeholder="Add a note of encouragement" className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream resize-none mb-4" />
        <button onClick={pledge} disabled={busy} data-testid="donation-submit"
          className="w-full min-h-[56px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
          <Send size={22} strokeWidth={2.5} /> {busy ? "Sending…" : "Pledge to the fund"}
        </button>
        <p className="text-sm text-forest-500 mt-3 text-center">Pledges are recorded to celebrate generosity. No payment is taken in this version.</p>
      </div>

      {/* Recent supporters */}
      <h2 className="font-head text-2xl font-bold text-forest-900 mb-4">Recent supporters</h2>
      {!data ? (
        <div className="h-24 rounded-2xl bg-white/60 border-2 border-border animate-pulse" />
      ) : data.items.length === 0 ? (
        <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">Be the first to support the community fund.</p>
      ) : (
        <div className="space-y-3" data-testid="donation-list">
          {data.items.map((d) => (
            <div key={d.donation_id} className="bg-white rounded-2xl border-2 border-border p-4 flex items-center gap-4">
              <Avatar user={d} size={48} />
              <div className="min-w-0">
                <p className="font-bold text-forest-900">{d.name}</p>
                {d.message && <p className="text-forest-700 text-sm truncate">{d.message}</p>}
                <p className="text-forest-500 text-xs">{timeAgo(d.created_at)}</p>
              </div>
              <span className="ml-auto shrink-0 font-head text-xl font-bold text-terracotta">{fmt(d.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
